import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type {
  Evaluation,
  EvaluatorAssignment,
  Procurement,
  Rubric,
  RubricCriteria,
  Supplier,
} from '../../types/procurement';
import EvaluationRubricBuilder from './EvaluationRubricBuilder';

interface Props {
  procurement: Procurement;
  suppliers: Supplier[];
  evaluations: Evaluation[];
  evaluatorAssignments: EvaluatorAssignment[];
  rubric?: Rubric | null;
  isPanelChair?: boolean;
  onSave?: (updatedData: Partial<Procurement>) => Promise<void>;
  onSaveRubric?: (criteria: RubricCriteria[]) => Promise<void>;
}

const EMPTY_CRITERIA: RubricCriteria[] = [];

const ProcurementOverview: React.FC<Props> = ({
  procurement,
  suppliers,
  evaluations,
  evaluatorAssignments,
  rubric,
  isPanelChair,
  onSave,
  onSaveRubric,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const [description, setDescription] = useState(procurement.description || '');
  const [status, setStatus] = useState(procurement.status || 'DRAFT');

  const [executiveSummary, setExecutiveSummary] = useState(
    procurement.rfpContent?.executiveSummary || ''
  );
  const [timeline, setTimeline] = useState(procurement.rfpContent?.timeline || '');
  const [scopeOfWork, setScopeOfWork] = useState(procurement.rfpContent?.scopeOfWork || '');
  const [complianceRequirements, setComplianceRequirements] = useState<string[]>(
    procurement.complianceRequirements || []
  );

  useEffect(() => {
    setDescription(procurement.description || '');
    setStatus(procurement.status || 'DRAFT');
    setExecutiveSummary(procurement.rfpContent?.executiveSummary || '');
    setTimeline(procurement.rfpContent?.timeline || '');
    setScopeOfWork(procurement.rfpContent?.scopeOfWork || '');
    setComplianceRequirements(procurement.complianceRequirements || []);
  }, [procurement]);

  const getStatusColor = (statusValue: string): 'default' | 'info' | 'warning' | 'success' => {
    switch (statusValue) {
      case 'DRAFT':
        return 'default';
      case 'PUBLISHED':
        return 'info';
      case 'EVALUATION':
        return 'warning';
      case 'MODERATION':
        return 'warning';
      case 'AWARDED':
        return 'success';
      case 'ARCHIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave({
        description,
        status: status as Procurement['status'],
        complianceRequirements,
        rfpContent: {
          ...procurement.rfpContent,
          executiveSummary,
          timeline,
          scopeOfWork,
        },
      });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setDescription(procurement.description || '');
    setStatus(procurement.status || 'DRAFT');
    setExecutiveSummary(procurement.rfpContent?.executiveSummary || '');
    setTimeline(procurement.rfpContent?.timeline || '');
    setScopeOfWork(procurement.rfpContent?.scopeOfWork || '');
    setComplianceRequirements(procurement.complianceRequirements || []);
    setIsEditing(false);
  };

  const handleAddRequirement = () => {
    setComplianceRequirements([...complianceRequirements, '']);
  };

  const handleRemoveRequirement = (index: number) => {
    const newReqs = [...complianceRequirements];
    newReqs.splice(index, 1);
    setComplianceRequirements(newReqs);
  };

  const handleRequirementChange = (index: number, value: string) => {
    const newReqs = [...complianceRequirements];
    newReqs[index] = value;
    setComplianceRequirements(newReqs);
  };

  const totalSuppliers = suppliers.length;

  // Since Panel Chairs can submit evaluations without an explicit assignment,
  // we define the total expected as the unique set of (evaluator, supplier) pairs
  // from both assignments AND actual evaluations.
  const allExpectedPairs = new Set([
    ...evaluatorAssignments.map((a) => `${a.userId}_${a.supplierId}`),
    ...evaluations.map((e) => `${e.evaluatorId}_${e.supplierId}`),
  ]);

  const totalExpectedEvaluations = allExpectedPairs.size;

  // A pair is completed if there is a SUBMITTED evaluation for it
  const completedEvaluations = Array.from(allExpectedPairs).filter((pair) => {
    const [evaluatorId, supplierId] = pair.split('_');
    return evaluations.some(
      (e) =>
        e.evaluatorId === evaluatorId && e.supplierId === supplierId && e.status === 'SUBMITTED'
    );
  }).length;

  const evaluationProgress =
    totalExpectedEvaluations > 0 ? (completedEvaluations / totalExpectedEvaluations) * 100 : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Top Row: Stats & Progress */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader sx={{ bgcolor: 'action.hover' }} title="Important Dates" />
            <Divider />
            <CardContent>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemText
                    primary="Created On"
                    secondary={new Date(procurement.createdAt).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Submission Deadline"
                    secondary={new Date(procurement.dueDate).toLocaleDateString()}
                    primaryTypographyProps={{ color: 'error.main', fontWeight: 'bold' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader sx={{ bgcolor: 'action.hover' }} title="Evaluation Progress" />
            <Divider />
            <CardContent>
              <Typography variant="h3" color="primary.main" gutterBottom>
                {totalSuppliers}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Total Submissions Received
              </Typography>

              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Evaluation Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={evaluationProgress}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {Math.round(evaluationProgress)}%
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                {completedEvaluations} out of {totalExpectedEvaluations} required evaluations
                submitted
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content: Metadata */}
      <Card>
        <CardHeader
          sx={{ bgcolor: 'action.hover' }}
          title="Procurement Overview"
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {procurement.extractionStatus === 'PROCESSING' && (
                <Chip
                  icon={<CircularProgress size={16} />}
                  label="Extracting Details..."
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {procurement.extractionStatus === 'ERROR' && (
                <Chip
                  label="Extraction Failed"
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {procurement.extractionStatus === 'COMPLETED' && (
                <Chip
                  label="Extraction Complete"
                  color="success"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}

              {isEditing ? (
                <TextField
                  select
                  size="small"
                  value={['EVALUATION', 'AWARDED'].includes(status) ? 'PUBLISHED' : status}
                  onChange={(e) => setStatus(e.target.value as Procurement['status'])}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="PUBLISHED">Published</MenuItem>
                  <MenuItem value="ARCHIVED">Archived</MenuItem>
                </TextField>
              ) : (
                <Chip
                  label={procurement.status}
                  color={getStatusColor(procurement.status)}
                  sx={{ fontWeight: 'bold' }}
                />
              )}

              {isPanelChair &&
                onSave &&
                (isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                ))}
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {isEditing && status !== 'DRAFT' && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
                color: 'warning.contrastText',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
              >
                <WarningAmberIcon sx={{ mr: 1, fontSize: 20 }} />
                To edit procurement details, please change the Status back to DRAFT.
              </Typography>
            </Box>
          )}

          {isEditing ? (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 4 }}
              disabled={status !== 'DRAFT'}
            />
          ) : (
            <Typography variant="body1" sx={{ mb: 4 }}>
              {procurement.description}
            </Typography>
          )}

          {procurement.rfpDocuments && procurement.rfpDocuments.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Source Documents
              </Typography>
              <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {procurement.rfpDocuments.map((doc) => (
                  <Typography
                    key={doc.id}
                    variant="body2"
                    color="textSecondary"
                    noWrap
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '0.75rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    • {doc.name}
                  </Typography>
                ))}
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                RFP Details
              </Typography>
            </Box>
          </Box>

          {isEditing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
              <TextField
                label="Executive Summary"
                fullWidth
                multiline
                minRows={3}
                value={executiveSummary}
                onChange={(e) => setExecutiveSummary(e.target.value)}
                disabled={status !== 'DRAFT'}
              />
              <TextField
                label="Timeline"
                fullWidth
                multiline
                minRows={2}
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                disabled={status !== 'DRAFT'}
              />
              <TextField
                label="Scope of Work"
                fullWidth
                multiline
                minRows={4}
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                disabled={status !== 'DRAFT'}
              />
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                >
                  Mandatory Compliance Requirements
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {complianceRequirements.map((req, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: simple string array editing
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                        value={req}
                        onChange={(e) => handleRequirementChange(index, e.target.value)}
                        placeholder={`Requirement ${index + 1}`}
                        disabled={status !== 'DRAFT'}
                      />
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveRequirement(index)}
                        disabled={status !== 'DRAFT'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={handleAddRequirement}
                    sx={{ alignSelf: 'flex-start' }}
                    disabled={status !== 'DRAFT'}
                  >
                    Add Requirement
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                >
                  Executive Summary
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {procurement.rfpContent?.executiveSummary || 'Not provided'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                >
                  Timeline
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {procurement.rfpContent?.timeline || 'Not provided'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                >
                  Scope of Work
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {procurement.rfpContent?.scopeOfWork || 'Not provided'}
                </Typography>
              </Box>

              {procurement.complianceRequirements &&
                procurement.complianceRequirements.length > 0 && (
                  <Box>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}
                    >
                      Mandatory Compliance Requirements
                    </Typography>
                    <List
                      dense
                      disablePadding
                      sx={{
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {procurement.complianceRequirements.map((req, index) => (
                        <ListItem
                          key={req}
                          divider={index < (procurement.complianceRequirements?.length || 0) - 1}
                        >
                          <ListItemText
                            primary={req}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
            </Box>
          )}

          {onSaveRubric && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <EvaluationRubricBuilder
                initialCriteria={rubric?.criteria || EMPTY_CRITERIA}
                readOnly={!isEditing || status !== 'DRAFT'}
                onSave={async (criteria) => {
                  await onSaveRubric(criteria);
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProcurementOverview;
